import { useEffect, useRef, useState } from "react";
import { Download, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { errText, papi } from "../portalApi";

export const mk = (s) => { const k = String(s || "").toUpperCase().split(/\s+/).filter(Boolean).join(" ").replace(/\./g, "\uff0e"); return k.startsWith("$") ? "\uff04" + k.slice(1) : k; };
export const inr = (n) => (n == null || n === "" ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);
export const marginPct = (sale, cost) => (Number(sale) > 0 && Number(cost) > 0 ? Math.round(((sale - cost) / sale) * 1000) / 10 : null);

const block = (e) => { if (!(e.target instanceof HTMLInputElement)) e.preventDefault(); };
/* View-only price tables: no selecting, copying, right-click, dragging or printing (typing in sale boxes still works). */
export const NoCopy = ({ children, className = "", testid, on = true }) => (on ?
  <div className={`select-none print:hidden [&_input]:select-text ${className}`} style={{ WebkitUserSelect: "none", userSelect: "none", WebkitTouchCallout: "none" }}
    onCopy={(e) => e.preventDefault()} onCut={(e) => e.preventDefault()} onContextMenu={block} onDragStart={(e) => e.preventDefault()} data-testid={testid}>{children}</div>
  : <div className={className} data-testid={testid}>{children}</div>
);


export const Margin = ({ sale, cost, testid }) => {
  const m = marginPct(sale, cost);
  if (m == null) return <span className="text-slate-300">—</span>;
  return <span className={`font-semibold ${m < 0 ? "text-rose-600" : m < 15 ? "text-amber-600" : "text-emerald-700"}`} data-testid={testid}>{m}%</span>;
};

/* Product box that suggests Price list products after 3 letters (hidden products never shown). */
export const ProductInput = ({ value, onChange, onPick, testid }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const t = useRef(null);
  useEffect(() => {
    clearTimeout(t.current);
    if (!open || String(value || "").trim().length < 3) { setItems([]); return undefined; }
    t.current = setTimeout(() => papi("get", `/pricelist/suggest?q=${encodeURIComponent(value.trim())}`).then((r) => setItems(r.items)).catch(() => setItems([])), 250);
    return () => clearTimeout(t.current);
  }, [value, open]);
  return (
    <div className="relative">
      <input value={value} onChange={(e) => { onChange(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 200)}
        className="w-full min-w-[180px] rounded border px-2 py-1" placeholder="Type 3 letters…" data-testid={testid} />
      {open && items.length > 0 && (
        <div className="absolute left-0 top-full z-30 mt-1 max-h-64 w-[420px] overflow-y-auto rounded-lg border bg-white text-xs shadow-xl" data-testid={`${testid}-suggest`}>
          {items.map((x) => (
            <button key={x.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { onPick(x); setOpen(false); }}
              className="flex w-full items-start gap-2 border-b px-2 py-1.5 text-left hover:bg-teal-50" data-testid={`${testid}-opt-${x.id}`}>
              <span className="min-w-0 flex-1"><b className="block text-slate-800">{x.name}</b>
                <span className="text-[10px] text-slate-500">{[x.generic, x.company, x.packing && `Pack ${x.packing}`].filter(Boolean).join(" · ")}</span></span>
              <span className="whitespace-nowrap text-right text-[10px]">{x.cost != null && <span className="block font-semibold text-slate-700">Cost {inr(x.cost)}</span>}
                {x.mrp != null && <span className="text-slate-400">MRP {inr(x.mrp)}</span>}</span>
            </button>))}
        </div>)}
    </div>
  );
};

/* Download template + upload filled sheet for a dropship client (kind = products | shipping). */
export const SheetButtons = ({ cid, kind, onDone }) => {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const template = async () => {
    try {
      const b = await papi("get", `/dropship/template?kind=${kind}`, null, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(b); a.download = kind === "shipping" ? "shipping_sale_prices.xlsx" : "product_sale_prices.xlsx"; a.click();
    } catch (e) { toast.error(errText(e)); }
  };
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await papi("post", `/dropship/clients/${cid}/upload?kind=${kind}`, fd);
      toast.success(`${r.added} added · ${r.updated} updated${r.unmatched_count ? ` · ${r.unmatched_count} not in ${kind === "shipping" ? "shipping prices" : "price list"}` : ""}`);
      onDone?.(r);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <span className="inline-flex gap-1">
      <button onClick={template} className="inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-xs text-slate-600" data-testid={`ds-${kind}-template`}><Download className="h-3.5 w-3.5" />Template</button>
      <button onClick={() => ref.current?.click()} disabled={busy} className="inline-flex items-center gap-1 rounded-lg border border-teal-300 bg-teal-50 px-2 py-1 text-xs font-semibold text-teal-800" data-testid={`ds-${kind}-upload`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}Upload</button>
      <input ref={ref} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid={`ds-${kind}-upload-input`} />
    </span>
  );
};
